import { useState, useRef, useEffect } from 'react';
import { videoPosterSrc } from '@/components/SocialImage';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';

const VideoModal = ({ isOpen, onClose, video }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setPlaying(false);
      setProgress(0);
    }
  }, [isOpen]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); }
    else { videoRef.current.play(); }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
  };

  const handleSeek = (e) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pct * videoRef.current.duration;
  };

  const handleFullscreen = () => {
    videoRef.current?.requestFullscreen?.();
  };

  if (!isOpen || !video) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
        onClick={onClose}>
        <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          transition={{ type: 'spring', damping: 28 }}
          className="relative w-full max-w-3xl"
          onClick={e => e.stopPropagation()} data-testid="video-modal">

          {/* Close button */}
          <button onClick={onClose}
            className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            data-testid="video-modal-close">
            <X className="w-4 h-4 text-white" />
          </button>

          {/* Video */}
          <div className="rounded-xl overflow-hidden bg-black relative group">
            <video
              ref={videoRef}
              muted={muted}
              playsInline
              preload="metadata"
              poster={videoPosterSrc(video?.src) || undefined}
              onTimeUpdate={handleTimeUpdate}
              onEnded={() => setPlaying(false)}
              onClick={togglePlay}
              className="w-full aspect-video cursor-pointer"
              data-testid="video-player"
            >
              <source src={video.src} type="video/mp4" />
            </video>

            {/* Play overlay (before first play) */}
            {!playing && progress === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer" onClick={togglePlay}>
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors">
                  <Play className="w-7 h-7 text-white ml-1" fill="white" />
                </div>
              </div>
            )}

            {/* Controls */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Progress bar */}
              <div className="h-1 bg-white/20 rounded-full mb-3 cursor-pointer" onClick={handleSeek} data-testid="video-progress">
                <div className="h-full bg-taxera-teal rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={togglePlay} className="text-white hover:text-taxera-teal transition-colors" data-testid="video-play-btn">
                    {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button onClick={() => setMuted(!muted)} className="text-white hover:text-taxera-teal transition-colors" data-testid="video-mute-btn">
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>
                <button onClick={handleFullscreen} className="text-white hover:text-taxera-teal transition-colors" data-testid="video-fullscreen-btn">
                  <Maximize className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Title & description */}
          <div className="mt-4">
            <h3 className="text-lg font-bold text-white">{video.title}</h3>
            <p className="text-sm text-white/60 mt-1">{video.description}</p>
            <span className="text-2xs text-white/40 mt-2 block">{video.duration}</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VideoModal;
